import React, { useEffect, useRef, useState } from "react";
import { TextField, Button, Box, Typography, IconButton, Tooltip } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import { useTranslation } from "react-i18next";

const CInput2 = ({
    cId,
    cVariant,
    cHintText,
    cDisabled,
    cHelperText,
    cOtherMsg,
    cOtherType,
    cType,
    cRows,
    cOnChange,
    cHtmlProps,
    cInputProps,
    cDropDown,
    cValue,
    cKeyReset,
    cHandleSubmit,
    cOnBlur,
    cIsSmall,
    cIsRequired,
    cFullWidth,
    cEndAdornment,
    cIsSession,
    cOnUnlock
}) => {
    const { t } = useTranslation();

    const [selectedFiles, setSelectedFiles] = useState([]);
    const fileInputRef = useRef(null);
    const [localValue, setLocalValue] = useState(cValue)

    useEffect(() => {
        setLocalValue(cValue)
    }, [cValue])

    if (localValue === undefined) {
        const sessionItem = sessionStorage.getItem(cId);
        setLocalValue(sessionItem);
    }

    const handleButtonClick = () => {
        if (fileInputRef.current && !cDisabled) {
            fileInputRef.current.click();
        }
    };


    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(files);
        if (cOnChange !== undefined) {
            cOnChange(event.target.files);
        }
    };

    const getFileText = () => {
        if (selectedFiles.length === 0) {
            return t('no_file_chosen', 'No file chosen');
        } else if (selectedFiles.length === 1) {
            return selectedFiles[0].name;
        } else {
            return `${selectedFiles.length} ${t('files_selected', 'files selected')}`;
        }
    };

    const getValidationMessage = () => {
        return t('please_fill_this_field', 'Please fill out this field');
    };

    if (cType === "file") {
        return (
            <Box sx={{ width: cFullWidth ? '100%' : 'auto' }}>
                <Box sx={{
                    border: '1px solid #c4c4c4',
                    borderRadius: '4px',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: cDisabled ? '#f5f5f5' : 'white',
                    position: 'relative',
                    '&:hover': {
                        borderColor: cDisabled ? '#c4c4c4' : '#000'
                    }
                }}>
                    <input
                        ref={fileInputRef}
                        id={cId}
                        type="file"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        disabled={cDisabled}
                        required={cIsRequired}
                        title={cIsRequired ? getValidationMessage() : ""}
                        {...cHtmlProps}
                    />

                    <Button
                        size={cIsSmall ? "small" : "medium"}
                        disabled={cDisabled}
                        onClick={handleButtonClick}
                        sx={{
                            mr: 2,
                            textTransform: 'none',
                            fontSize: cIsSmall ? '0.75rem' : '0.875rem',
                            border: '1px solid #c4c4c4',
                            padding: '1px 10px'
                        }}
                    >
                        {t('choose_files', 'Choose files')}
                    </Button>

                    <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                            fontSize: cIsSmall ? '0.75rem' : '0.875rem',
                            flex: 1
                        }}
                    >
                        {getFileText()}
                    </Typography>
                </Box>
                {(cHelperText || cOtherMsg) && (
                    <Typography
                        variant="caption"
                        color={cOtherType === "error" ? "error" : "textSecondary"}
                        sx={{
                            display: 'block',
                            mt: 0.5,
                            ml: 1.75,
                            fontSize: '0.75rem'
                        }}
                    >
                        {cOtherMsg || cHelperText}
                    </Typography>
                )}
            </Box>
        );
    }

    return (
        <TextField
            id={cId}
            key={cKeyReset}
            disabled={cDisabled}
            label={cHintText === undefined ? "" : cHintText}
            color={cOtherType === "success" ? "success" : "primary"}
            focused={cOtherType === "success"}
            variant={cVariant === undefined ? "outlined" : cVariant}
            error={cOtherType === "error"}
            type={cType === undefined ? "text" : (cType === "quantity" ? "text" : cType)}
            helperText={cOtherMsg === undefined ? (cHelperText === undefined ? " " : cHelperText) : cOtherMsg}
            multiline={cRows > 1 ? true : false}
            inputProps={{
                ...cHtmlProps,
                // inputProps.onBlur lands on the native <input> and takes
                // priority over the top-level onBlur below (MUI wires up
                // whichever inputProps supplies, bypassing its own chain to
                // the top-level prop) — so if cHtmlProps already carries an
                // onBlur (useKeyboardUnlock's re-lock), composing both here
                // is the only way to guarantee cOnBlur still fires too,
                // instead of being silently shadowed on touch devices.
                onBlur: (event) => {
                    cHtmlProps?.onBlur?.(event);
                    cOnBlur?.(event);
                },
                title: cIsRequired ? getValidationMessage() : "",
                // type="number" ignores inputMode="none" on several Android
                // browsers (confirmed: quantity field on Galaxy Tab A11) —
                // the native numeric keypad opens on first tap regardless of
                // the lock. Using type="text" with inputMode="numeric"
                // instead is the standard, reliably-honored way to get a
                // numeric keypad, and still respects "none" while locked.
                inputMode: cHtmlProps?.inputMode === "none"
                    ? "none"
                    : (cType === "quantity" ? "numeric" : cHtmlProps?.inputMode)
            }}
            InputProps={(cEndAdornment || cOnUnlock) ? {
                endAdornment: (
                    <InputAdornment position="end">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {cOnUnlock && !cDisabled && cHtmlProps?.inputMode === "none" && (
                                <Tooltip title={t("open_keyboard", "Open keyboard")}>
                                    <IconButton size="small" tabIndex={-1} onClick={cOnUnlock}>
                                        <KeyboardIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {cEndAdornment}
                        </Box>
                    </InputAdornment>
                ),
            } : cInputProps}
            onChange={(event) => {
                setLocalValue(event.target.value)
                if (cOnChange !== undefined) {
                    if (cType === "quantity") {
                        let changeValue = event.target.value;
                        const parsedValue = parseInt(changeValue);
                        if (parsedValue < 0) {
                            event.target.value = 0;
                            changeValue = 0;
                        }
                        cOnChange(changeValue);
                        if (cIsSession) {
                            sessionStorage.setItem(cId, changeValue);
                        }
                    } else {
                        const changeValue = event.target.value;
                        cOnChange(changeValue);
                        if (cIsSession) {
                            sessionStorage.setItem(cId, changeValue);
                        }
                    }
                }
            }}
            rows={cRows === undefined ? 10 : cRows}
            fullWidth={cFullWidth}
            {...cDropDown}
            // defaultValue={cValue}
            value={localValue}
            onKeyDown={(event) => {
                if (cHandleSubmit !== undefined && event.key === "Enter") {
                    cHandleSubmit();
                }
                if (cType === "quantity") {
                    if (event.key === '.' || event.key === '-') {
                        event.preventDefault();
                    }
                }
            }}
            required={cIsRequired}
            size={cIsSmall ? "small" : "medium"}

            
        />
    );
};

export default CInput2;